<template>
  <div class="automation-detail-page">
    <AppPageHeader design="new" :title="`Follow-up Automation · Run #${$route.params.run_id}`" compact>
      <template #actions><AppRefreshButton :loading="refreshing" @click="refresh" /></template>
    </AppPageHeader>
    <p v-if="!runId" role="alert" class="automation-problem">Invalid run ID.</p>
    <div v-else class="automation-stack automation-detail-content">
      <AppTabs design="new" refined weight="medium" :tabs="tabs" :value="tab" @input="changeTab" />
      <ResourceState new-ui :resource="detail" label="run" @reload="refresh">
        <template #default="{ data: run }">
          <div class="automation-detail-grid">
            <DetailCard title="Lead">
              <template v-if="run.current_lead_stage" #actions>
                <AppStatusBadge
                  design="new"
                  compact
                  :color="run.current_lead_stage.color"
                  :label="display(run.current_lead_stage)"
                />
              </template>
              <dl class="automation-fields">
                <div>
                  <dt>Lead</dt>
                  <dd class="automation-lead-links">
                    <span>{{ run.lead.name }}</span>
                    <router-link
                      v-tooltip="{ content: 'Open lead details', placement: 'top', offset: 8 }"
                      :to="{ name: 'leads-detail-lead', params: { id: run.lead.id } }"
                      class="automation-lead-links__icon"
                      aria-label="Open lead details"
                    >
                      <img src="@/assets/img/table-link-icon.svg" alt="">
                    </router-link>
                    <router-link
                      v-tooltip="{ content: 'Show in lead flow', placement: 'top', offset: 8 }"
                      :to="{ name: 'crm-task-tracker', query: { lead: String(run.lead.id) } }"
                      class="automation-lead-links__icon automation-lead-links__icon--flow"
                      aria-label="Show in lead flow"
                    >
                      <img src="@/assets/img/checklist-icon.svg" alt="">
                    </router-link>
                  </dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{{ display(run.lead.email) }}</dd>
                </div>
                <div>
                  <dt>Phone</dt>
                  <dd>{{ display(run.lead.phone) }}</dd>
                </div>
                <div>
                  <dt>Manager</dt>
                  <dd>{{ display(run.current_lead_manager) }}</dd>
                </div>
              </dl>
            </DetailCard>

            <DetailCard title="Automation">
              <template #actions>
                <AppStatusBadge design="new" compact :tone="statusTone(run.status)" :label="display(run.status)" />
                <AppStatusBadge
                  v-if="run.has_attention"
                  design="new"
                  compact
                  tone="amber"
                  :label="`Needs attention · ${run.attention_count}`"
                />
              </template>
              <dl class="automation-fields">
                <div>
                  <dt>Policy</dt>
                  <dd
                    v-tooltip="{
                      content: `${run.policy.code} · v${run.policy.version}`,
                      placement: 'top',
                      offset: 8,
                      classes: 'tooltip--new'
                    }"
                    tabindex="0"
                  >
                    {{ run.policy.name }}
                  </dd>
                </div>
                <div>
                  <dt>Stage at start</dt>
                  <dd>
                    <AppStatusBadge
                      v-if="run.stage"
                      design="new"
                      compact
                      :color="run.stage.color"
                      :label="display(run.stage)"
                    />
                    <span v-else>—</span>
                  </dd>
                </div>
                <div>
                  <dt>Manager at start</dt>
                  <dd>{{ display(run.manager_at_start) }}</dd>
                </div>
                <div>
                  <dt>Progress</dt>
                  <dd
                    v-tooltip="{
                      content: `${processedCount(run.progress)} of ${run.progress.total} follow-ups processed`,
                      placement: 'top',
                      offset: 8,
                      classes: 'tooltip--new'
                    }"
                    class="automation-progress"
                    tabindex="0"
                  >
                    <LeadFollowUpProgress
                      :progress="run.progress"
                      :state="run.status.code"
                      :failed="run.has_attention"
                    />
                  </dd>
                </div>
              </dl>
            </DetailCard>

            <DetailCard title="Timeline">
              <dl class="automation-fields">
                <div>
                  <dt>Started</dt>
                  <dd>{{ dateTime(run.started_at) }}</dd>
                </div>
                <div>
                  <dt>Started automatically</dt>
                  <dd>
                    <AppStatusBadge
                      design="new"
                      compact
                      :tone="run.auto_started ? 'green' : 'neutral'"
                      :label="run.auto_started ? 'Yes' : 'No'"
                    />
                  </dd>
                </div>
                <div>
                  <dt>
                    <span
                      v-tooltip="{
                        content: 'The communication date normally used to calculate every scheduled follow-up step. An overdue sequence may be shifted forward while preserving its intervals.',
                        placement: 'top',
                        offset: 8,
                        classes: 'tooltip--new'
                      }"
                      class="automation-help-label"
                      tabindex="0"
                    >Anchor</span>
                  </dt>
                  <dd>{{ dateTime(run.anchor_at) }}</dd>
                </div>
                <div v-if="run.schedule_adjustment">
                  <dt>Schedule adjustment</dt>
                  <dd>{{ run.schedule_adjustment.reason }}</dd>
                </div>
                <div v-if="run.schedule_adjustment && run.schedule_adjustment.scheduled_first_due_at">
                  <dt>Adjusted first delivery</dt>
                  <dd>{{ dateTime(run.schedule_adjustment.scheduled_first_due_at) }}</dd>
                </div>
                <div v-if="run.next_scheduled_at">
                  <dt>
                    <span
                      v-tooltip="{
                        content: 'Scheduled steps are calculated from Anchor. A past date means the next planned step is overdue.',
                        placement: 'top',
                        offset: 8,
                        classes: 'tooltip--new'
                      }"
                      class="automation-help-label"
                      tabindex="0"
                    >
                      {{ isOverdue(run.next_scheduled_at) ? 'Overdue since' : 'Next scheduled' }}
                    </span>
                  </dt>
                  <dd>{{ dateTime(run.next_scheduled_at) }}</dd>
                </div>
                <div v-if="run.completed_at">
                  <dt>Completed</dt>
                  <dd>{{ dateTime(run.completed_at) }}</dd>
                </div>
                <div v-if="run.cancelled_at">
                  <dt>Cancelled</dt>
                  <dd>{{ dateTime(run.cancelled_at) }}</dd>
                </div>
              </dl>
            </DetailCard>

            <DetailCard title="Control">
              <template #actions>
                <AppButton
                  v-if="run.delay.allowed"
                  design="new"
                  size="small"
                  :disabled="refreshing || !schedule.data"
                  @click.native="showDelay = true"
                >
                  Delay
                </AppButton>
              </template>
              <dl class="automation-fields">
                <div>
                  <dt>
                    <span
                      v-tooltip="{
                        content: 'Lead-level setting for automatic follow-ups. Enabled means automation is allowed to run for this lead.',
                        placement: 'top',
                        offset: 8,
                        classes: 'tooltip--new'
                      }"
                      class="automation-help-label"
                      tabindex="0"
                    >Automation control</span>
                  </dt>
                  <dd>
                    <AppStatusBadge
                      design="new"
                      compact
                      :tone="controlTone(run.control.mode)"
                      :label="display(run.control.mode)"
                    />
                  </dd>
                </div>
                <div v-if="run.control.reason">
                  <dt>Control reason</dt>
                  <dd>{{ run.control.reason }}</dd>
                </div>
                <div v-if="run.control.paused_at">
                  <dt>Paused at</dt>
                  <dd>{{ dateTime(run.control.paused_at) }}</dd>
                </div>
                <div v-if="run.control.paused_until">
                  <dt>Paused until</dt>
                  <dd>{{ dateTime(run.control.paused_until) }}</dd>
                </div>
                <div v-if="run.control.changed_by">
                  <dt>Control changed by</dt>
                  <dd>{{ display(run.control.changed_by) }}</dd>
                </div>
                <div v-if="run.control.changed_at">
                  <dt>Control changed at</dt>
                  <dd>{{ dateTime(run.control.changed_at) }}</dd>
                </div>
              </dl>
              <p v-if="!run.delay.allowed" class="automation-detail-card__note">
                {{ run.delay.unavailable_reason }}
              </p>
            </DetailCard>

            <DetailCard title="Anchor communication">
              <template v-if="run.anchor_communication" #actions>
                <AppButton
                  design="new"
                  theme="dark"
                  size="small"
                  @click.native="openCommunicationModal(run.anchor_communication.id)"
                >Show details</AppButton>
              </template>
              <dl v-if="run.anchor_communication" class="automation-fields">
                <div>
                  <dt>Date</dt>
                  <dd>{{ dateTime(run.anchor_communication.date) }}</dd>
                </div>
                <div>
                  <dt>Direction</dt>
                  <dd>
                    <AppStatusBadge
                      design="new"
                      compact
                      tone="blue"
                      :label="display(run.anchor_communication.direction)"
                    />
                  </dd>
                </div>
                <div>
                  <dt>Source</dt>
                  <dd>{{ display(run.anchor_communication.source) }}</dd>
                </div>
                <div>
                  <dt>Delivery status</dt>
                  <dd>
                    <AppStatusBadge
                      design="new"
                      compact
                      :tone="statusTone(run.anchor_communication.delivery_status)"
                      :label="display(run.anchor_communication.delivery_status)"
                    />
                  </dd>
                </div>
                <div v-if="run.cancellation_reason">
                  <dt>Cancellation reason</dt>
                  <dd>{{ display(run.cancellation_reason) }}</dd>
                </div>
                <div v-if="run.cancelled_by_communication">
                  <dt>Cancelled by communication</dt>
                  <dd>
                    #{{ run.cancelled_by_communication.id }} ·
                    {{ dateTime(run.cancelled_by_communication.date) }}
                  </dd>
                </div>
              </dl>
              <p v-else class="automation-muted">No anchor communication was recorded.</p>
            </DetailCard>

            <DetailCard
              new-ui
              v-if="run.attention_summary && run.attention_summary.length"
              title="Attention summary"
              wide
            >
              <div class="automation-attention-summary">
                <article
                  v-for="reason in run.attention_summary"
                  :key="`${reason.code}:${reason.level}`"
                  class="automation-attention-summary__item"
                >
                  <div class="automation-attention-summary__heading">
                    <AppStatusBadge
                      design="new"
                      compact
                      :tone="reason.level === 'send_failed' ? 'red' : 'amber'"
                      :label="reason.level === 'send_failed' ? 'Send failed' : 'Not sent'"
                    />
                    <strong>{{ reason.title }}</strong>
                    <span>{{ reason.affected_items }} affected</span>
                  </div>
                  <p>{{ reason.message }}</p>
                  <dl>
                    <div><dt>Code</dt><dd>{{ reason.code }}</dd></div>
                    <div><dt>Provider request</dt><dd>{{ reason.provider_attempted ? 'Yes' : 'No' }}</dd></div>
                    <div><dt>Automatic retry</dt><dd>{{ reason.automatic_retry_scheduled ? 'Scheduled' : 'Not scheduled' }}</dd></div>
                    <div><dt>Next action</dt><dd>{{ reason.recommended_action }}</dd></div>
                  </dl>
                </article>
              </div>
            </DetailCard>
          </div>
        </template>
      </ResourceState>
      <DelayForm
        v-if="showDelay"
        :run-id="runId"
        :detail="detail.data"
        :revision="schedule.data && schedule.data.schedule_revision"
        :refresh-version="refreshVersion"
        :refreshing="refreshing"
        @refresh="refresh"
        @close="showDelay = false"
        @saved="delayed"
      />
      <div v-if="tab === 'schedule'" class="automation-stack">
        <AppCard design="new" variant="table" header-density="compact" title="Schedule">
          <ResourceState new-ui :resource="schedule" label="schedule" @reload="refresh">
            <template #default="{ data }">
              <ScheduleActions
                :actions="data.actions"
                @open-delivery="openRouteModal"
                @open-communication="openCommunicationModal"
              />
            </template>
          </ResourceState>
        </AppCard>
        <AppCard
          design="new"
          variant="table"
          header-density="compact"
          title="Recorded history"
          subtitle="Saved events only. Earlier schedule revisions and status transitions may not have been recorded completely."
        >
          <PagedRecords new-ui :key="historyVersion" :fetch="fetchHistory" label="history records">
            <template #default="{ rows }">
              <HistoryTable :rows="rows" />
            </template>
          </PagedRecords>
        </AppCard>
      </div>
      <div v-else>
        <ResourceState new-ui :resource="otherRuns" label="other runs" @reload="loadOtherRuns">
          <template #default="{ data }">
            <RunTable show-id :data="data" :page="otherPage" :list-query="returnQuery" @page="changeOtherPage" />
          </template>
        </ResourceState>
      </div>
      <DeliveryModal
        v-if="selectedModalId"
        :key="`${runId}:${selectedModalId}`"
        :run-id="runId"
        :delivery-id="selectedModalId"
        @close="closeRouteModal"
        @open-communication="openCommunicationModal"
      />
      <AnchorCommunicationModal
        v-if="communicationModalId && detail.data"
        :key="`${detail.data.lead.id}:${communicationModalId}`"
        :lead-id="detail.data.lead.id"
        :communication-id="communicationModalId"
        @close="closeCommunicationModal"
      />
    </div>
  </div>
</template>
<script>
import {
  getFollowUpAutomationDetail,
  getFollowUpAutomationSchedule,
  getFollowUpAutomationHistory,
  getFollowUpAutomationRuns
} from '@/http'
import AppTabs from '@/components/AppTabs'
import AppRefreshButton from '@/components/AppRefreshButton.vue'
import { modalQueryId, modalRoute } from '@/views/crm/modalRoute'
import resources, { resource } from './resources'
import { positiveId, display, dateTime, statusTone, listState } from './model'
import ResourceState from './ResourceState.vue'
import PagedRecords from './PagedRecords.vue'
import HistoryTable from './HistoryTable.vue'
import RunTable from './RunTable.vue'
import DelayForm from './DelayForm.vue'
import DeliveryModal from './DeliveryModal.vue'
import ScheduleActions from './ScheduleActions.vue'
import DetailCard from './DetailCard.vue'
import LeadFollowUpProgress from '@/views/crm/task-tracker/LeadFollowUpProgress.vue'
import { processedCount } from '@/views/crm/task-tracker/leadFollowUpProgress'
import AnchorCommunicationModal from './AnchorCommunicationModal.vue'

export default {
  name: 'FollowUpAutomationDetail',
  components: {
    AppTabs,
    AppRefreshButton,
    ResourceState,
    PagedRecords,
    HistoryTable,
    RunTable,
    DelayForm,
    DeliveryModal,
    ScheduleActions,
    DetailCard,
    LeadFollowUpProgress,
    AnchorCommunicationModal
  },
  mixins: [resources, modalRoute('delivery')],
  data: () => ({
    detail: resource(),
    schedule: resource(),
    otherRuns: resource(),
    otherPage: 1,
    showDelay: false,
    refreshVersion: 0,
    historyVersion: 0,
    tabs: [
      { id: 'schedule', value: 'schedule', label: 'Schedule' },
      { id: 'other', value: 'other', label: 'Other Runs' }
    ]
  }),
  computed: {
    runId() {
      return positiveId(this.$route.params.run_id)
    },
    tab() {
      return this.$route.query.tab === 'other' ? 'other' : 'schedule'
    },
    returnQuery() {
      return listState(this.$route.query)
    },
    refreshing() {
      return this.detail.loading || this.schedule.loading
    },
    communicationModalId() {
      return modalQueryId(this.$route.query.communication)
    }
  },
  watch: {
    tab(value) {
      if (value === 'other') {
        this.otherPage = 1
        this.loadOtherRuns()
      }
    }
  },
  created() {
    if (this.runId) this.refresh()
  },
  methods: {
    processedCount,
    display,
    dateTime,
    statusTone,
    async refresh() {
      if (!this.runId) return
      this.historyVersion += 1
      const results = await Promise.all([
        this.loadResource(this.detail, () => getFollowUpAutomationDetail(this.runId)),
        this.loadResource(this.schedule, () => getFollowUpAutomationSchedule(this.runId))
      ])
      if (!this.workspaceAlive) return
      if (results.every(Boolean)) this.refreshVersion += 1
      if (this.tab === 'other') this.loadOtherRuns()
    },
    delayed() {
      this.showDelay = false
      this.$notify({ type: 'success', title: 'Delay applied.' })
      this.refresh()
    },
    changeTab(tab) {
      this.$router.push({ query: { ...this.$route.query, tab } }).catch(() => {})
    },
    fetchHistory(params) {
      return getFollowUpAutomationHistory(this.runId, params)
    },
    loadOtherRuns() {
      if (!this.detail.data) return
      const leadId = this.detail.data.lead.id
      this.loadResource(this.otherRuns, () => getFollowUpAutomationRuns({
        lead_id: leadId,
        exclude_run_id: this.runId,
        view: 'all',
        page: this.otherPage,
        page_size: 40
      })
      )
    },
    changeOtherPage(page) {
      this.otherPage = page
      this.loadOtherRuns()
    },
    openCommunicationModal(id) {
      const communicationId = modalQueryId(String(id))
      if (!communicationId) return
      this.$router.push({
        path: this.$route.path,
        query: { ...this.$route.query, communication: String(communicationId) },
        hash: this.$route.hash
      }).catch(() => {})
    },
    closeCommunicationModal() {
      const query = { ...this.$route.query }
      delete query.communication
      this.$router.replace({ path: this.$route.path, query, hash: this.$route.hash }).catch(() => {})
    },
    controlTone(mode) {
      const code = mode && mode.code ? mode.code : mode
      if (code === 'enabled') return 'green'
      if (code === 'paused') return 'amber'
      if (code === 'disabled') return 'red'
      return 'neutral'
    },
    isOverdue(value) {
      if (!value) return false
      const timestamp = new Date(value).getTime()
      return Number.isFinite(timestamp) && timestamp < Date.now()
    },
  }
}
</script>
<style scoped lang="scss">
.automation-detail-content > .tabs-list {
  margin-bottom: 0;
}
.automation-detail-page ::v-deep .automation-detail-grid {
  padding: 0;
  border-radius: 0;
  background: transparent;
}
.automation-detail-page .automation-detail-card .automation-fields > div {
  align-items: center;
}
.automation-attention-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $ui-space-12;
}
.automation-attention-summary__item {
  padding: $ui-space-12;
  border: 1px solid $ui-line;
  border-radius: $ui-control-radius;
  background: $ui-surface-soft;
}
.automation-attention-summary__heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $ui-space-8;
}
.automation-attention-summary__heading span:last-child {
  margin-left: auto;
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
}
.automation-attention-summary__item p {
  margin: $ui-space-8 0;
}
.automation-attention-summary__item dl {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $ui-space-8 $ui-space-16;
  margin: 0;
}
.automation-attention-summary__item dl div {
  min-width: 0;
}
.automation-attention-summary__item dt {
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
}
.automation-attention-summary__item dd {
  margin: 2px 0 0;
  overflow-wrap: anywhere;
}
@media (max-width: 760px) {
  .automation-attention-summary,
  .automation-attention-summary__item dl {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
